import React, { useEffect, useState } from 'react';

function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentCategory, setCurrentCategory] = useState({ id: '', name: '', image: '', link: '' });
  const [token] = useState(localStorage.getItem('adminToken'));

  useEffect(() => {
    const fetchCategories = async () => {
      if (!token) {
        setError('Unauthorized: Admin token missing');
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || 'Failed to fetch categories');
        }
        const data = await res.json();
        setCategories(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [token]);

  const openAddModal = () => {
    setIsEdit(false);
    setCurrentCategory({ id: '', name: '', image: '', link: '' });
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setIsEdit(true);
    setCurrentCategory({ id: category._id, name: category.name, image: category.image, link: category.link });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCategory((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const url = isEdit
        ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/categories/${currentCategory.id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/admin/categories`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: currentCategory.name,
          image: currentCategory.image,
          link: currentCategory.link,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'add'} category`);
      }
      setShowModal(false);
      const fetchCategories = async () => {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCategories(data);
      };
      fetchCategories();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/categories/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to delete category');
      }
      setCategories(categories.filter((cat) => cat._id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Categories</h2>
        <button className="btn btn-success rounded-pill py-2 px-3" onClick={openAddModal}>
          <strong><i className='bi bi-plus'></i></strong> Add Category
        </button>
      </div>

      {loading && <div>Loading categories...</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      {!loading && !error && categories.length === 0 && <div>No categories found.</div>}

      {!loading && !error && categories.length > 0 && (
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle mb-0">
            <thead className="table-success">
              <tr>
                <th>#</th>
                <th>Image</th>
                <th>Name</th>
                <th>Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat, idx) => (
                <tr key={cat._id}>
                  <td>{idx + 1}</td>
                  <td>
                    <img
                      src={cat.image || 'https://via.placeholder.com/60'}
                      alt={cat.name}
                      style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: '0.25rem' }}
                    />
                  </td>
                  <td>{cat.name}</td>
                  <td>{cat.link}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-success py-1 px-4 my-2 me-2"
                      onClick={() => openEditModal(cat)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger py-1 px-4 me-2"
                      onClick={() => handleDelete(cat._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{isEdit ? 'Edit Category' : 'Add Category'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={currentCategory.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Image URL</label>
                    <input
                      type="text"
                      className="form-control"
                      name="image"
                      value={currentCategory.image}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Link</label>
                    <input
                      type="text"
                      className="form-control"
                      name="link"
                      value={currentCategory.link}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {isEdit ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoriesPage;
